import { IconButton, Tooltip, useTheme } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { Printer } from 'iconsax-react';
import { type ReactElement, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { ServerErrorMessage } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { DEFAULT_COLUMN_HEADER_FORMATTER } from '../../../../pdf/components/PdfTable';
import { DataTableListPdf } from '../../../../pdf/DataTableListPdf';
import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';

type PrintButtonProps<TRow extends { id: string }> = {
  renderedColumns: ReadableSet<Column<TRow>>;
  getAllRows: () => Promise<TRow[]>;
  tableName: string;
};

export const PrintButton = <TRow extends { id: string }>({
  renderedColumns,
  getAllRows,
  tableName,
}: PrintButtonProps<TRow>): ReactElement => {
  const theme = useTheme();
  const toast = useToast();
  const [isOpeningPdf, setIsOpeningPdf] = useState(false);

  // The span keeps the tooltip working while the button is disabled.
  return (
    <Tooltip title='Print'>
      <span>
        <IconButton
          onClick={async (): Promise<void> => {
            setIsOpeningPdf(true);
            try {
              const rows = await getAllRows().catch((error) => {
                toast.error({
                  message: ServerErrorMessage(error, 'Failed to load the rows to print.'),
                });
                return undefined;
              });
              if (!hasValue(rows)) return;
              const pdfBlob = await pdf(
                <DataTableListPdf
                  columns={[...renderedColumns].map((col) => ({
                    columnHeaderToPdf: DEFAULT_COLUMN_HEADER_FORMATTER,
                    ...col,
                  }))}
                  rows={rows}
                  title={tableName}
                />,
              ).toBlob();
              const pdfDataUri = URL.createObjectURL(pdfBlob);
              window.open(pdfDataUri, '', '_blank');
            } catch {
              toast.error({ message: 'Failed to print, please try again.' });
            } finally {
              setIsOpeningPdf(false);
            }
          }}
          disabled={isOpeningPdf}
        >
          <Printer variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
